// src/components/navigation/NavigationLoader.tsx
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch } from '@/store/hooks';
import { setNavigation } from '@/components/theme-layouts/components/navigation/store/navigationSlice';
import baseNavigationConfig from '@/configs/navigationConfig';
import { useGetUserApplicationsQuery, useGetUserPermissionsQuery } from '@/app/(control-panel)/admin/settings/SettingsApi';
import { useGetAvailableBatchesCountQuery } from '@/app/(control-panel)/workflows/BatchesApi';
import { useGetPendingApprovalCountQuery } from '@/app/(control-panel)/approvals/ApprovalsApi';
import { visibleAdminChildren } from '@/configs/adminNavigation';

type NavItem = any;

function NavigationLoader() {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const isViewingWorkflows = pathname === '/workflows' || pathname.startsWith('/workflows/');
  const previousWorkflowStatus = useRef<string>();
  const [hasUnseenWorkflowChanges, setHasUnseenWorkflowChanges] = useState(false);

  // Server truth — ONLY this decides super admin visibility
  const { data: permsData, isLoading: permsLoading, refetch: refetchPerms } = useGetUserPermissionsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    pollingInterval: 30000,
  });

  // ✅ UPDATED: Add refetch for applications
  const { data: userApplications, isSuccess: appsLoaded, refetch: refetchApps } = useGetUserApplicationsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const { data: availableBatchesCount, refetch: refetchBatchCount } =
    useGetAvailableBatchesCountQuery(undefined, {
      pollingInterval: 30000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    });

  const { data: approvalsCountResp } = useGetPendingApprovalCountQuery(undefined, {
    pollingInterval: 30000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const approvalsCount = approvalsCountResp?.count ?? 0;

  const workflowStatus = availableBatchesCount
    ? [availableBatchesCount.count, availableBatchesCount.waitingForSubmission,
      availableBatchesCount.failed, availableBatchesCount.processing].join(':')
    : undefined;

  // Only changes after the initial snapshot are unseen. Viewing any workflow
  // page acknowledges them, including updates that arrive while it is open.
  useEffect(() => {
    if (isViewingWorkflows) {
      setHasUnseenWorkflowChanges(false);
    } else if (workflowStatus !== undefined && previousWorkflowStatus.current !== undefined
      && workflowStatus !== previousWorkflowStatus.current) {
      setHasUnseenWorkflowChanges(true);
    }
    previousWorkflowStatus.current = workflowStatus;
  }, [workflowStatus, isViewingWorkflows]);

  // Refresh on entry so a cached processing state does not linger after work ends.
  useEffect(() => {
    if (isViewingWorkflows) refetchBatchCount();
  }, [isViewingWorkflows, refetchBatchCount]);

  const workflowIconClass = availableBatchesCount?.processing > 0
    ? 'animate-fire'
    : hasUnseenWorkflowChanges && !isViewingWorkflows ? '!text-orange-500' : '';

  // Keep batch subtitle fresh
  useEffect(() => {
    const handleBatchStatusChange = () => refetchBatchCount();
    window.addEventListener('batchStatusChanged', handleBatchStatusChange);
    return () => window.removeEventListener('batchStatusChanged', handleBatchStatusChange);
  }, [refetchBatchCount]);

  // Optional: keep permissions fresh when user context might change
  useEffect(() => {
    const handleAuthChange = () => {
      refetchPerms();
      refetchApps();  // ✅ NEW: Also refetch apps on auth change
    };
    window.addEventListener('auth:changed', handleAuthChange);
    return () => window.removeEventListener('auth:changed', handleAuthChange);
  }, [refetchPerms, refetchApps]);

  // ✅ NEW: Application change listener - fires when apps are created/deleted/updated
  useEffect(() => {
    const handleApplicationChange = () => {
      refetchApps();
    };
    window.addEventListener('applicationChanged', handleApplicationChange);
    return () => window.removeEventListener('applicationChanged', handleApplicationChange);
  }, [refetchApps]);

  const isSuperAdminStrict = permsData?.user?.isSuperAdmin === true; // ⬅️ single source of truth

  // 🔥 NEW: Get main category permissions
    const hasWorkflowCategory = permsData?.subcategoryPermissions?.hasWorkflowCategory === true;
    const hasApplicationCategory = permsData?.subcategoryPermissions?.hasApplicationCategory === true;
    // CAPTURE / AGENTS group toggles gate the Capture and Agents nav items
    // (RequireCategory guards the matching routes so the URL cannot be typed in either).
    const hasCaptureCategory = permsData?.subcategoryPermissions?.hasCaptureCategory === true;
    const hasAgentsCategory = permsData?.subcategoryPermissions?.hasAgentsCategory === true;

  // Permission-based tab visibility (super admin OR admin-role users with specific permission)
    const userRole = permsData?.user?.role?.toLowerCase();
    const isAdminRole = userRole === 'admin';
    const hasAddWorkflows = isAdminRole && permsData?.subcategoryPermissions?.hasAddWorkflows === true;
    const hasCreateApplications = isAdminRole && permsData?.subcategoryPermissions?.hasCreateApplications === true;
    // Admin nav group: the group's ADMIN category toggle AND the admin role. The role check mirrors
    // RequireAdmin on the admin routes, so a user-role member of an ADMIN-toggled group is not
    // offered links that bounce to the error page. (Super admins bypass this in the filter.)
    const hasAdminCategory = isAdminRole && permsData?.subcategoryPermissions?.hasAdminCategory === true;


  const applicationNavItems = useMemo<NavItem[]>(() => {
    if (!appsLoaded || !userApplications) return [];
    return userApplications.map((app: any) => ({
      id: app.id,
      title: app.name,
      type: 'item',
      icon: 'heroicons-outline:cube',
      url: `/apps/${app.id}`,
    }));
  }, [appsLoaded, userApplications]);

  const workflowSubtitle = useMemo(() => {
    if (!availableBatchesCount || !availableBatchesCount.count) return '';
    const { count } = availableBatchesCount;
    return `${count} Batch${count !== 1 ? 'es' : ''} Available`;
  }, [availableBatchesCount]);

  // 🔥 UPDATED: Filter by superOnly, requiredPermission, AND main category permissions
  const filterByPermissions = (items: NavItem[], isSA: boolean, permissions: any): NavItem[] =>
    (items || [])
      .map((item) => {
        // Admin group (see hasAdminCategory above). Checked before the children branch: the group
        // always has children, so the per-item checks further down never see the group itself.
        if (item.id === 'admin') {
          const children = visibleAdminChildren(item.children ?? [], isSA, permissions.hasAdminCategory, permissions.canManageGroups);
          if (children.length === 0) return null;
          return { ...item, children: filterByPermissions(children, isSA, permissions) };
        }

        if (item?.children?.length) {
          const children = filterByPermissions(item.children, isSA, permissions);
          // If all children are filtered out, filter the parent too (unless it's a superadmin override)
          if (children.length === 0 && !isSA) return null;
          return { ...item, children };
        }

        // 1. Check superOnly flag (only super admin can see)
        const superOnly = item?.meta?.superOnly === true;
        if (superOnly && !isSA) return null;
        if (item?.meta?.adminOnly && !isSA && !isAdminRole) return null;

        // 2. Check requiredPermission flag (super admin OR users with specific permission)
        const requiredPermission = item?.meta?.requiredPermission;
        if (requiredPermission && !isSA && !permissions[requiredPermission]) {
          return null;
        }

        // 3. Check main category permissions for specific nav items
        if (!isSA) {
          if (item.id === 'workflow-component' && !permissions.hasWorkflowCategory) {
            return null;
          }
          if (item.id === 'applications-component' && !permissions.hasApplicationCategory) {
            return null;
          }
          if (item.id === 'capture-component' && !permissions.hasCaptureCategory) {
            return null;
          }
          if (item.id === 'director-agents-component' && !permissions.hasAgentsCategory) {
            return null;
          }
        }

        return item;
      })
      .filter(Boolean) as NavItem[];

  useEffect(() => {
    // Wait until we know perms (or at least have a boolean)
    if (permsLoading || !appsLoaded) return;

    let updated = baseNavigationConfig.map((item: NavItem) => {
      if (item.id === 'applications-component') {
        return { ...item, children: applicationNavItems };
      }
      if (item.id === 'workflow-component') {
        return {
          ...item,
          subtitle: workflowSubtitle,
          iconClass: workflowIconClass,
          badge: undefined
        };
      }
      if (item.id === 'approvals-component' && approvalsCount > 0) {
        return { ...item, badge: { title: String(approvalsCount), bg: '#dc2626', fg: '#ffffff' } };
      }
      return item;
    });

    // 🔥 UPDATED: Pass permissions object (includes main categories + sub-permission flags)
    const permissions = {
      hasWorkflowCategory,
      hasApplicationCategory,
      hasAdminCategory,
      hasCaptureCategory,
      hasAgentsCategory,
      hasAddWorkflows,
      hasCreateApplications,
      canManageGroups: isAdminRole,
    };
    updated = filterByPermissions(updated, isSuperAdminStrict, permissions);

   // console.log('🔥 NavigationLoader - Final navigation:', updated.map(i => i.id));

    // Important: always dispatch a *fresh* array so changes propagate
    dispatch(setNavigation(updated));
  }, [
    dispatch,
    permsLoading,
    isSuperAdminStrict,
    hasWorkflowCategory,
    hasApplicationCategory,
    hasAdminCategory,
    hasCaptureCategory,
    hasAgentsCategory,
    hasAddWorkflows,
    hasCreateApplications,
    isAdminRole,
    appsLoaded,
    applicationNavItems,
    workflowIconClass,
    workflowSubtitle,
    approvalsCount,
  ]);

  return null;
}

export default NavigationLoader;
